<template>
  <div class="family-selector">
    <div class="selected-family" v-if="modelValue">
      <FamilyCard :family-id="modelValue" 
        show-profile-type 
        select-mode 
        @click="removeSelectedFamily"/>
    </div>
    <div v-else>
      <SearchInput ref="searchInputRef" 
        v-model="searchTextRef" 
        :search-results="filteredSearchResultsRef" 
        :placeholder="placeholderRef"
        :disabled="disabled"
        v-slot="{ item }"
        @focus="emit('focus')"
        @result-selected="searchResultSelected">
        <FamilyListItem :family-id="item.id!" :father="getFather(item as ViewFamily)" :mother="getMother(item as ViewFamily)" />
      </SearchInput>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.selected-family {
  margin: 0 0.5rem;

  .family-list-item {
    margin: 0;
  }
}

.search-input {
  .family-list-item {
    margin: 0.25rem 0;
  }
}
</style>

<script lang="ts" setup>
import { computed, defineProps, nextTick, ref, watch } from 'vue'
import { useLiveFamilySearch } from '@/rd/LivePersonSearch'
import { livingWindowStart, ViewFamily } from '@/rd/ResearchDataModel'
import FamilyCard from './FamilyCard.vue'
import SearchInput from "@/util/SearchInput.vue"
import FamilyListItem from './FamilyListItem.vue'
import { useViewPersonStore } from '@/rd/ViewPersonStore'

const props = defineProps<{
  modelValue: string | undefined
  livingOnly?: boolean
  disabled?: boolean
  placeholder?: string
  postFilter?: (vps: ViewFamily[]) => Promise<ViewFamily[]>
}>()

const emit = defineEmits([
  'update:modelValue',
  'focus'
])

defineExpose({
  focus: () => searchInputRef.value?.focus(),
  reset
})

const removeButtonRef = ref<HTMLButtonElement>()
const searchInputRef = ref<InstanceType<typeof SearchInput>>()

const viewPersonStore = useViewPersonStore()

const placeholderRef = computed(() => props.placeholder ?? "Type a name")
const { searchTextRef, searchResultsRef } = useLiveFamilySearch(props.postFilter)

const filteredSearchResultsRef = computed(() => {
  const results = searchResultsRef.value
  if (props.livingOnly) {
    return results.filter(vf => {
      const faBirthYear = getFather(vf)?.birthYear
      const moBirthYear = getMother(vf)?.birthYear
      const latestBirthYear = Math.max(faBirthYear ?? 0, moBirthYear ?? 0)
      return latestBirthYear >= livingWindowStart
    })
  }
  return results
})

function getFather(vf: ViewFamily) {
  return viewPersonStore.getLoadedPerson(vf.fatherId)
}

function getMother(vf: ViewFamily) {
  return viewPersonStore.getLoadedPerson(vf.motherId)
}

async function searchResultSelected(id: string) {
  emit('update:modelValue', id)
  await nextTick()
  removeButtonRef.value?.focus()
}

async function removeSelectedFamily() {
  emit('update:modelValue', undefined)
  await nextTick()
  searchInputRef.value?.focus()
}

function reset() {
  searchTextRef.value = ""
  searchInputRef.value?.reset()
}

</script>